import { Map, List, Set } from 'immutable';

import createTypedReducer from '../createTypedReducer';
import * as deviceGroupsActions from './action';
import { CHANGE_CLIENT, CLEAR } from '../authentication/action';

export const initialState = Map({
  groups: Map({
    data: Map(),
    records: List(),
    loading: false,
    total: 0,
    totalPages: 0,
    // ids of rows currently lazy-loading their full device list
    expandingDevices: Set(),
  }),
  // The group open on the edit screen. Held apart from `groups.data` because the listing's copy of
  // a group may carry only part of its devices, and editing must never seed from a partial list.
  editing: Map({
    id: null,
    record: Map(),
    loading: false
  }),
});

function resetState (state) {
  return state.withMutations((nextState) =>
    nextState.set('groups', Map({
      data: Map(),
      records: List(),
      loading: false,
      total: 0,
      totalPages: 0
    })).set('editing', Map({
      id: null,
      record: Map(),
      loading: false,
      totalPages: 0,
      expandingDevices: Set(),
    }))
  );
}

export const deviceGroupsReducer = createTypedReducer(initialState, {

  [deviceGroupsActions.FETCH_DEVICE_GROUPS] (state) {
    return state.withMutations((nextState) => {
      nextState.setIn(['groups', 'loading'], true);
    });
  },

  [deviceGroupsActions.FETCH_DEVICE_GROUPS_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      const fetchDate = new Date();
      let newDataIds = List();
      nextState.setIn(['groups', 'data'], Map({}));
      action.groups.map((group) => {
        newDataIds = newDataIds.push(group.get('id'));
        nextState.setIn(['groups', 'data', group.get('id')], {
          record: group,
          lastFetch: fetchDate,
        });
      });
      nextState.setIn(['groups', 'records'], newDataIds);
      nextState.setIn(['groups', 'total'], action.total);
      nextState.setIn(['groups', 'totalPages'], action.totalPages);
      nextState.setIn(['groups', 'loading'], false);
    });
  },

  [deviceGroupsActions.FETCH_DEVICE_GROUPS_LABELS_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      const fetchDate = new Date();
      action.groups.map((group) => {
        const curGroup = nextState.getIn(['groups', 'data', group.get('id'), 'record'], Map());
        const mergedGroup = curGroup.merge(group);
        nextState.setIn(['groups', 'data', group.get('id')], {
          record: mergedGroup,
          lastFetch: fetchDate,
        });
      });
    });
  },

  [deviceGroupsActions.FETCH_DEVICE_GROUPS_FAILED] (state) {
    return state.withMutations((nextState) =>
      nextState.setIn(['groups', 'loading'], false)
    );
  },

  [deviceGroupsActions.DELETE_DEVICE_GROUP_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      nextState = nextState.deleteIn(['groups', 'data',  action.groupId]);
      const newIds = nextState.getIn(['groups',  'records'], []);
      nextState = nextState.setIn(['groups',  'records'], newIds.filter(x => x !== action.groupId));
      nextState.setIn(['loading'], false);

      return nextState;
    });
  },

  [deviceGroupsActions.FETCH_DEVICE_GROUP_FOR_EDIT] (state) {
    return state.withMutations((nextState) => {
      nextState.setIn(['editing', 'loading'], true);

      return nextState;
    });
  },
  [deviceGroupsActions.FETCH_DEVICE_GROUP_FOR_EDIT_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      nextState.setIn(['editing', 'id'], action.group.get('id'));
      nextState.setIn(['editing', 'record'], action.group);
      nextState.setIn(['editing', 'loading'], false);

      return nextState;
    });
  },
  [deviceGroupsActions.CREATE_DEVICE_GROUP_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      const currentList = nextState.getIn(['groups', 'records'], []);
      nextState = nextState.setIn(['groups', 'data', action.id, 'record'], Map({
        group_name: action.groupName,
        id: action.id,
        devices: action.deviceIds,
        count: action.count
      }))
        .setIn(['groups', 'records'], currentList.push(action.id))
        .setIn(['groups', 'loading'], false);

      return nextState;
    });
  },


  // Per-row loading rather than the table-wide `loading` flag: only the expanded row is waiting,
  // so only that row should show as busy.
  [deviceGroupsActions.EXPAND_DEVICE_GROUP_DEVICES] (state, action) {
    return state.withMutations((nextState) => {
      nextState.updateIn(['groups', 'expandingDevices'], Set(), (ids) => ids.add(action.groupId));
    });
  },

  [deviceGroupsActions.EXPAND_DEVICE_GROUP_DEVICES_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      const current = nextState.getIn(['groups', 'data', action.groupId, 'record'], Map());
      // the response is the whole membership, so what is shown now equals the count
      nextState.setIn(['groups', 'data', action.groupId, 'record'], current.merge(Map({
        devices: action.devices,
        count: action.devices ? action.devices.size : 0
      })));
      nextState.updateIn(['groups', 'expandingDevices'], Set(), (ids) => ids.remove(action.groupId));
    });
  },

  [deviceGroupsActions.EXPAND_DEVICE_GROUP_DEVICES_FAILED] (state, action) {
    return state.withMutations((nextState) => {
      nextState.updateIn(['groups', 'expandingDevices'], Set(), (ids) => ids.remove(action.groupId));
    });
  },

  [deviceGroupsActions.UPDATE_DEVICE_GROUP] (state) {
    return state.withMutations((nextState) => {
      nextState.setIn(['groups', 'loading'], true);
    });
  },

  [deviceGroupsActions.UPDATE_DEVICE_GROUP_SUCCESS] (state, action) {
    return state.withMutations((nextState) => {
      nextState.setIn(['groups', 'data', action.groupId, 'record'], Map({
        group_name: action.groupName,
        id: action.groupId,
        devices: action.deviceIds,
        count: action.count
      }));
      nextState.setIn(['groups', 'loading'], false);
    });
  },

  [deviceGroupsActions.UPDATE_DEVICE_GROUP_FAILED] (state) {
    return state.withMutations((nextState) => {
      nextState.setIn(['groups', 'loading'], false);
    });
  },

  [deviceGroupsActions.FETCH_DEVICE_GROUP_FOR_EDIT_FAILED] (state,) {
    return state.withMutations((nextState) => {
      nextState.setIn(['editing', 'loading'], false);
      return nextState;
    });
  },

  [CLEAR] (state) {
    return resetState(state);
  },

  [CHANGE_CLIENT] (state) {
    return resetState(state);
  }
});

export default deviceGroupsReducer;
